// src/lib/coordinateGrid/equationParser.ts
/**
 * Equation Parser
 * 
 * Parses mathematical equation strings into structured data.
 * Supports formats:
 * - y = mx + c (linear, slope-intercept form)
 * - y = ax² + bx + c (quadratic)
 * - x = k (vertical line)
 * - y = k (horizontal line)
 * - ax + by = c (standard form - convert internally)
 * 
 * Phase 2: Graphing Equations
 */

import { ParsedEquation, EquationType, EquationCoefficients, Intercept, Point2D, InequalityType, PlotEquation } from '../../types/coordinateGrid';

/**
 * Parse an equation string into structured data
 */
export function parseEquation(input: string): ParsedEquation {
  // Normalize input: trim whitespace, handle case
  const normalized = input.trim().replace(/\s+/g, ' ').toLowerCase();
  
  if (!normalized) {
    return {
      type: 'linear',
      coefficients: {},
      expression: input,
      isValid: false,
      error: 'Empty equation',
    };
  }

  // Try different equation formats
  // 1. Vertical line: x = k
  const verticalMatch = normalized.match(/^x\s*=\s*(-?\d+(?:\.\d+)?)$/);
  if (verticalMatch) {
    const k = parseFloat(verticalMatch[1]);
    return {
      type: 'vertical',
      coefficients: { k },
      expression: input,
      isValid: true,
    };
  }

  // 2. Horizontal line: y = k
  const horizontalMatch = normalized.match(/^y\s*=\s*(-?\d+(?:\.\d+)?)$/);
  if (horizontalMatch) {
    const k = parseFloat(horizontalMatch[1]);
    return {
      type: 'horizontal',
      coefficients: { k },
      expression: input,
      isValid: true,
    };
  }

  // 3. Linear: y = mx + c or y = mx or y = x + c or y = x
  const linearMatch = normalized.match(/^y\s*=\s*(-?\d+(?:\.\d+)?)?\s*x\s*([+-]\s*\d+(?:\.\d+)?)?\s*$/);
  if (linearMatch) {
    const mStr = linearMatch[1];
    const cStr = linearMatch[2];
    
    const m = mStr ? parseFloat(mStr) : (normalized.includes('x') ? 1 : 0);
    const c = cStr ? parseFloat(cStr.replace(/\s+/g, '')) : 0;
    
    return {
      type: 'linear',
      coefficients: { m, c },
      expression: input,
      isValid: true,
    };
  }

  // 4. Linear: y = c (when no x term)
  const linearConstantMatch = normalized.match(/^y\s*=\s*(-?\d+(?:\.\d+)?)$/);
  if (linearConstantMatch) {
    const c = parseFloat(linearConstantMatch[1]);
    return {
      type: 'linear',
      coefficients: { m: 0, c },
      expression: input,
      isValid: true,
    };
  }

  // 5. Quadratic: y = ax² + bx + c (various formats)
  // Match: y = ax^2 + bx + c, y = ax² + bx + c, y = a*x^2 + b*x + c
  const quadraticMatch = normalized.match(/^y\s*=\s*(-?\d+(?:\.\d+)?)?\s*x\s*[²\^]\s*2\s*([+-]\s*\d+(?:\.\d+)?)?\s*x\s*([+-]\s*\d+(?:\.\d+)?)?\s*$/);
  if (quadraticMatch) {
    const aStr = quadraticMatch[1];
    const bStr = quadraticMatch[2];
    const cStr = quadraticMatch[3];
    
    const a = aStr ? parseFloat(aStr) : 1;
    const b = bStr ? parseFloat(bStr.replace(/\s+/g, '')) : 0;
    const c = cStr ? parseFloat(cStr.replace(/\s+/g, '')) : 0;
    
    return {
      type: 'quadratic',
      coefficients: { a, b, c },
      expression: input,
      isValid: true,
    };
  }

  // 6. Quadratic: y = ax² + c (no bx term)
  const quadraticNoBMatch = normalized.match(/^y\s*=\s*(-?\d+(?:\.\d+)?)?\s*x\s*[²\^]\s*2\s*([+-]\s*\d+(?:\.\d+)?)?\s*$/);
  if (quadraticNoBMatch) {
    const aStr = quadraticNoBMatch[1];
    const cStr = quadraticNoBMatch[2];
    
    const a = aStr ? parseFloat(aStr) : 1;
    const c = cStr ? parseFloat(cStr.replace(/\s+/g, '')) : 0;
    
    return {
      type: 'quadratic',
      coefficients: { a, b: 0, c },
      expression: input,
      isValid: true,
    };
  }

  // 7. Standard form: ax + by = c (convert to slope-intercept)
  const standardFormMatch = normalized.match(/^(-?\d+(?:\.\d+)?)?\s*x\s*([+-]\s*\d+(?:\.\d+)?)?\s*y\s*=\s*(-?\d+(?:\.\d+)?)$/);
  if (standardFormMatch) {
    const aStr = standardFormMatch[1];
    const bStr = standardFormMatch[2];
    const cStr = standardFormMatch[3];
    
    const a = aStr ? parseFloat(aStr) : 1;
    const b = bStr ? parseFloat(bStr.replace(/\s+/g, '')) : 1;
    const c = parseFloat(cStr);
    
    if (b === 0) {
      // Vertical line: ax = c => x = c/a
      return {
        type: 'vertical',
        coefficients: { k: c / a },
        expression: input,
        isValid: true,
      };
    }
    
    // Convert to y = mx + c form
    const m = -a / b;
    const cConverted = c / b;
    
    return {
      type: 'linear',
      coefficients: { m, c: cConverted },
      expression: input,
      isValid: true,
    };
  }

  // If no pattern matches, return invalid
  return {
    type: 'linear',
    coefficients: {},
    expression: input,
    isValid: false,
    error: 'Unrecognized equation format',
  };
}

/**
 * Evaluate y for a given x value
 */
export function evaluateY(equation: ParsedEquation, x: number): number | null {
  if (!equation.isValid) {
    return null;
  }

  const { type, coefficients } = equation;

  switch (type) {
    case 'vertical':
      // Vertical lines don't have a y value for a given x
      return null;
    
    case 'horizontal':
      // Horizontal line: y = k
      return coefficients.k ?? null;
    
    case 'linear': {
      // Linear: y = mx + c
      const m = coefficients.m ?? 0;
      const c = coefficients.c ?? 0;
      return m * x + c;
    }
    
    case 'quadratic': {
      // Quadratic: y = ax² + bx + c
      const a = coefficients.a ?? 0;
      const b = coefficients.b ?? 0;
      const c = coefficients.c ?? 0;
      return a * x * x + b * x + c;
    }
    
    default:
      return null;
  }
}

/**
 * Find intercepts for an equation
 */
export function findIntercepts(equation: ParsedEquation): Intercept[] {
  const intercepts: Intercept[] = [];

  if (!equation.isValid) {
    return intercepts;
  }

  const { type, coefficients } = equation;

  switch (type) {
    case 'vertical': {
      // x-intercept only: (k, 0)
      const k = coefficients.k;
      if (k !== undefined) {
        intercepts.push({
          type: 'x',
          value: k,
          point: { x: k, y: 0 },
        });
      }
      break;
    }
    
    case 'horizontal': {
      // y-intercept only: (0, k)
      const k = coefficients.k;
      if (k !== undefined) {
        intercepts.push({
          type: 'y',
          value: k,
          point: { x: 0, y: k },
        });
      }
      break;
    }
    
    case 'linear': {
      const m = coefficients.m ?? 0;
      const c = coefficients.c ?? 0;
      
      // y-intercept: (0, c)
      intercepts.push({
        type: 'y',
        value: c,
        point: { x: 0, y: c },
      });
      
      // x-intercept: where y = 0, so 0 = mx + c => x = -c/m (if m ≠ 0)
      if (m !== 0) {
        const xIntercept = -c / m;
        intercepts.push({
          type: 'x',
          value: xIntercept,
          point: { x: xIntercept, y: 0 },
        });
      }
      break;
    }
    
    case 'quadratic': {
      const a = coefficients.a ?? 0;
      const b = coefficients.b ?? 0;
      const c = coefficients.c ?? 0;
      
      // y-intercept: (0, c)
      intercepts.push({
        type: 'y',
        value: c,
        point: { x: 0, y: c },
      });
      
      // x-intercepts: solve ax² + bx + c = 0
      if (a !== 0) {
        const discriminant = b * b - 4 * a * c;
        
        if (discriminant > 0) {
          // Two real roots
          const sqrtD = Math.sqrt(discriminant);
          const x1 = (-b + sqrtD) / (2 * a);
          const x2 = (-b - sqrtD) / (2 * a);
          
          intercepts.push(
            {
              type: 'x',
              value: x1,
              point: { x: x1, y: 0 },
            },
            {
              type: 'x',
              value: x2,
              point: { x: x2, y: 0 },
            }
          );
        } else if (discriminant === 0) {
          // One real root (tangent)
          const x = -b / (2 * a);
          intercepts.push({
            type: 'x',
            value: x,
            point: { x, y: 0 },
          });
        }
        // If discriminant < 0, no real x-intercepts
      }
      break;
    }
  }

  return intercepts;
}

/**
 * Find intersection points between two equations
 */
export function findIntersections(eq1: ParsedEquation, eq2: ParsedEquation): Point2D[] {
  const intersections: Point2D[] = [];

  if (!eq1.isValid || !eq2.isValid) {
    return intersections;
  }

  // Handle vertical lines
  if (eq1.type === 'vertical' && eq2.type === 'vertical') {
    // Parallel vertical lines: no intersection (unless same line)
    if (eq1.coefficients.k === eq2.coefficients.k) {
      // Same line: infinite intersections, return empty
    }
    return intersections;
  }

  if (eq1.type === 'vertical') {
    const k = eq1.coefficients.k;
    if (k === undefined) return intersections;
    const y = evaluateY(eq2, k);
    if (y !== null) {
      intersections.push({ x: k, y });
    }
    return intersections;
  }

  if (eq2.type === 'vertical') {
    const k = eq2.coefficients.k;
    if (k === undefined) return intersections;
    const y = evaluateY(eq1, k);
    if (y !== null) {
      intersections.push({ x: k, y });
    }
    return intersections;
  }

  // Both are horizontal
  if (eq1.type === 'horizontal' && eq2.type === 'horizontal') {
    // Parallel horizontal lines: no intersection (unless same line)
    return intersections;
  }

  // One horizontal, one other
  if (eq1.type === 'horizontal') {
    const y = eq1.coefficients.k;
    if (y === undefined) return intersections;
    // Solve eq2 for x where y = k
    // This depends on eq2 type
    if (eq2.type === 'linear') {
      const m = eq2.coefficients.m ?? 0;
      const c = eq2.coefficients.c ?? 0;
      if (m !== 0) {
        const x = (y - c) / m;
        intersections.push({ x, y });
      }
    } else if (eq2.type === 'quadratic') {
      const a = eq2.coefficients.a ?? 0;
      const b = eq2.coefficients.b ?? 0;
      const c = eq2.coefficients.c ?? 0;
      // Solve: y = ax² + bx + c => 0 = ax² + bx + (c - y)
      const discriminant = b * b - 4 * a * (c - y);
      if (discriminant >= 0 && a !== 0) {
        const sqrtD = Math.sqrt(discriminant);
        const x1 = (-b + sqrtD) / (2 * a);
        const x2 = (-b - sqrtD) / (2 * a);
        intersections.push({ x: x1, y }, { x: x2, y });
      }
    }
    return intersections;
  }

  if (eq2.type === 'horizontal') {
    const y = eq2.coefficients.k;
    if (y === undefined) return intersections;
    if (eq1.type === 'linear') {
      const m = eq1.coefficients.m ?? 0;
      const c = eq1.coefficients.c ?? 0;
      if (m !== 0) {
        const x = (y - c) / m;
        intersections.push({ x, y });
      }
    } else if (eq1.type === 'quadratic') {
      const a = eq1.coefficients.a ?? 0;
      const b = eq1.coefficients.b ?? 0;
      const c = eq1.coefficients.c ?? 0;
      const discriminant = b * b - 4 * a * (c - y);
      if (discriminant >= 0 && a !== 0) {
        const sqrtD = Math.sqrt(discriminant);
        const x1 = (-b + sqrtD) / (2 * a);
        const x2 = (-b - sqrtD) / (2 * a);
        intersections.push({ x: x1, y }, { x: x2, y });
      }
    }
    return intersections;
  }

  // Linear-Linear intersection
  if (eq1.type === 'linear' && eq2.type === 'linear') {
    const m1 = eq1.coefficients.m ?? 0;
    const c1 = eq1.coefficients.c ?? 0;
    const m2 = eq2.coefficients.m ?? 0;
    const c2 = eq2.coefficients.c ?? 0;
    
    // Parallel lines: no intersection
    if (m1 === m2) {
      return intersections;
    }
    
    // Solve: m1*x + c1 = m2*x + c2
    const x = (c2 - c1) / (m1 - m2);
    const y = m1 * x + c1;
    intersections.push({ x, y });
    return intersections;
  }

  // Linear-Quadratic intersection
  if (eq1.type === 'linear' && eq2.type === 'quadratic') {
    const m = eq1.coefficients.m ?? 0;
    const c = eq1.coefficients.c ?? 0;
    const a = eq2.coefficients.a ?? 0;
    const b = eq2.coefficients.b ?? 0;
    const c2 = eq2.coefficients.c ?? 0;
    
    // Solve: mx + c = ax² + bx + c2
    // => 0 = ax² + (b - m)x + (c2 - c)
    const A = a;
    const B = b - m;
    const C = c2 - c;
    
    if (A === 0) {
      // Degenerate to linear
      if (B !== 0) {
        const x = -C / B;
        const y = m * x + c;
        intersections.push({ x, y });
      }
    } else {
      const discriminant = B * B - 4 * A * C;
      if (discriminant >= 0) {
        const sqrtD = Math.sqrt(discriminant);
        const x1 = (-B + sqrtD) / (2 * A);
        const x2 = (-B - sqrtD) / (2 * A);
        intersections.push(
          { x: x1, y: m * x1 + c },
          { x: x2, y: m * x2 + c }
        );
      }
    }
    return intersections;
  }

  if (eq1.type === 'quadratic' && eq2.type === 'linear') {
    // Swap and solve
    return findIntersections(eq2, eq1);
  }

  // Quadratic-Quadratic intersection (can have up to 4 points)
  if (eq1.type === 'quadratic' && eq2.type === 'quadratic') {
    const a1 = eq1.coefficients.a ?? 0;
    const b1 = eq1.coefficients.b ?? 0;
    const c1 = eq1.coefficients.c ?? 0;
    const a2 = eq2.coefficients.a ?? 0;
    const b2 = eq2.coefficients.b ?? 0;
    const c2 = eq2.coefficients.c ?? 0;
    
    // Solve: a1*x² + b1*x + c1 = a2*x² + b2*x + c2
    // => (a1 - a2)*x² + (b1 - b2)*x + (c1 - c2) = 0
    const A = a1 - a2;
    const B = b1 - b2;
    const C = c1 - c2;
    
    if (A === 0) {
      // Degenerate to linear
      if (B !== 0) {
        const x = -C / B;
        const y = a1 * x * x + b1 * x + c1;
        intersections.push({ x, y });
      }
    } else {
      const discriminant = B * B - 4 * A * C;
      if (discriminant >= 0) {
        const sqrtD = Math.sqrt(discriminant);
        const x1 = (-B + sqrtD) / (2 * A);
        const x2 = (-B - sqrtD) / (2 * A);
        intersections.push(
          { x: x1, y: a1 * x1 * x1 + b1 * x1 + c1 },
          { x: x2, y: a1 * x2 * x2 + b1 * x2 + c1 }
        );
      }
    }
    return intersections;
  }

  return intersections;
}

/**
 * Generate points for rendering an equation curve
 */
export function sampleEquation(
  equation: ParsedEquation,
  xMin: number,
  xMax: number,
  sampleCount: number = 200
): Point2D[] {
  const points: Point2D[] = [];

  if (!equation.isValid) {
    return points;
  }

  const { type } = equation;

  if (type === 'vertical') {
    // Vertical line: just return two points at the x value
    const k = equation.coefficients.k;
    if (k !== undefined && k >= xMin && k <= xMax) {
      // Use a large y range for vertical lines
      points.push({ x: k, y: -1000 }, { x: k, y: 1000 });
    }
    return points;
  }

  if (type === 'horizontal') {
    // Horizontal line: just return two points
    const k = equation.coefficients.k;
    if (k !== undefined) {
      points.push({ x: xMin, y: k }, { x: xMax, y: k });
    }
    return points;
  }

  if (type === 'linear') {
    // Linear: just need 2 points
    const y1 = evaluateY(equation, xMin);
    const y2 = evaluateY(equation, xMax);
    if (y1 !== null && y2 !== null) {
      points.push({ x: xMin, y: y1 }, { x: xMax, y: y2 });
    }
    return points;
  }

  if (type === 'quadratic') {
    // Quadratic: sample many points for smooth curve
    const step = (xMax - xMin) / (sampleCount - 1);
    for (let i = 0; i < sampleCount; i++) {
      const x = xMin + i * step;
      const y = evaluateY(equation, x);
      if (y !== null && isFinite(y)) {
        points.push({ x, y });
      }
    }
    return points;
  }

  return points;
}

// ============================================================================
// INEQUALITY PARSING (Phase 7)
// ============================================================================

/**
 * Parse inequality expression
 */
export function parseInequality(input: string): {
  equation: ParsedEquation;
  type: InequalityType;
  isValid: boolean;
  error?: string;
} {
  // Normalize input: trim whitespace
  const normalized = input.trim();

  if (!normalized) {
    return {
      equation: {
        type: 'linear',
        coefficients: {},
        expression: input,
        isValid: false,
        error: 'Empty inequality',
      },
      type: '<',
      isValid: false,
      error: 'Empty inequality',
    };
  }

  // Match inequality patterns: y < mx + c, y > mx + c, y <= mx + c, y >= mx + c
  // Also support: y < 2x + 1, y > 3, x < 5, etc.
  
  // Pattern for y <expression> <operator> <expression>
  const inequalityMatch = normalized.match(/^y\s*(<|>|<=|>=)\s*(.+)$/);
  if (inequalityMatch) {
    const operator = inequalityMatch[1] as InequalityType;
    const rightSide = inequalityMatch[2].trim();
    
    // Parse the right side as an equation
    const parsed = parseEquation(`y = ${rightSide}`);
    
    if (parsed.isValid) {
      return {
        equation: parsed,
        type: operator,
        isValid: true,
      };
    }
  }

  // Pattern for x <expression> <operator> <value>
  const xInequalityMatch = normalized.match(/^x\s*(<|>|<=|>=)\s*(-?\d+(?:\.\d+)?)$/);
  if (xInequalityMatch) {
    const operator = xInequalityMatch[1] as InequalityType;
    const k = parseFloat(xInequalityMatch[2]);
    
    return {
      equation: {
        type: 'vertical',
        coefficients: { k },
        expression: `x = ${k}`,
        isValid: true,
      },
      type: operator,
      isValid: true,
    };
  }

  // If no pattern matches, return invalid
  return {
    equation: {
      type: 'linear',
      coefficients: {},
      expression: input,
      isValid: false,
      error: 'Unrecognized inequality format',
    },
    type: '<',
    isValid: false,
    error: 'Unrecognized inequality format',
  };
}

/**
 * Test if a point is in the solution region of an inequality
 */
export function isPointInRegion(
  point: Point2D,
  inequality: { type: InequalityType; boundaryEquation: PlotEquation }
): boolean {
  const { type, boundaryEquation } = inequality;
  const parsed = parseEquation(boundaryEquation.expression);
  
  if (!parsed.isValid) {
    return false;
  }

  // For vertical lines: x = k
  if (parsed.type === 'vertical') {
    const k = parsed.coefficients.k;
    if (k === undefined) return false;
    
    switch (type) {
      case '<':
        return point.x < k;
      case '>':
        return point.x > k;
      case '<=':
        return point.x <= k;
      case '>=':
        return point.x >= k;
      default:
        return false;
    }
  }

  // For other equation types, evaluate y at point's x
  const boundaryY = evaluateY(parsed, point.x);
  if (boundaryY === null) {
    return false;
  }

  // Compare point's y with boundary y based on inequality type
  switch (type) {
    case '<':
      return point.y < boundaryY;
    case '>':
      return point.y > boundaryY;
    case '<=':
      return point.y <= boundaryY;
    case '>=':
      return point.y >= boundaryY;
    default:
      return false;
  }
}

